/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetActiveCompetitionsRow, GetCompetitionsRow, GetCompetitionRow, GetCompetitionStandingsRow, GetCompetitionFairPnlStandingsRow, GetCompetitionCashProfitStandingsRow, GetCompetitionMyStandingRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String } from '../../helpers/index.js';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetActiveCompetitions ────────────────────────────────────────────────────────────

export interface useGetActiveCompetitionsArgs { readonly viewportGated?: boolean; }


async function _useGetActiveCompetitionsQueryFn(
  args: useGetActiveCompetitionsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetActiveCompetitionsRow>> {
  const url = '/v1/competitions/active';
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetActiveCompetitions',
    routeTemplate: '/v1/competitions/active',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetActiveCompetitionsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetActiveCompetitionsFactory = Object.assign(
  (args: useGetActiveCompetitionsArgs) => ({
    queryKey: ['competitions', 'list'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetActiveCompetitionsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetActiveCompetitions',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['competitions', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetActiveCompetitionsArgs, PagedData<GetActiveCompetitionsRow>>;


export const useGetActiveCompetitionsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions/active',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetActiveCompetitionsFactory },
  parseInput: (raw: unknown): useGetActiveCompetitionsArgs => raw as useGetActiveCompetitionsArgs,
  parseResponse: (raw: unknown): PagedData<GetActiveCompetitionsRow> => raw as PagedData<GetActiveCompetitionsRow>,
  specName: 'useGetActiveCompetitions' as const,
  polling: { hasNats: false, intervalMs: 10000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetActiveCompetitionsRow>>;
export const useGetActiveCompetitionsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetActiveCompetitionsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetActiveCompetitionsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetActiveCompetitionsPerEntitySubjects(_args: useGetActiveCompetitionsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetActiveCompetitionsRowSubjectTemplates(_args: useGetActiveCompetitionsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetActiveCompetitionsApplyEvent(
  prev: PagedData<GetActiveCompetitionsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetActiveCompetitionsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetActiveCompetitionsRealtime = {
  domain: 'competitions' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetActiveCompetitionsArgs) => _useGetActiveCompetitionsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetActiveCompetitionsArgs) => _useGetActiveCompetitionsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetActiveCompetitionsArgs) =>
    (prev: PagedData<GetActiveCompetitionsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetActiveCompetitionsRow> | undefined =>
      _useGetActiveCompetitionsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetActiveCompetitionsRow>, unknown>;


// ─── useGetCompetitions ────────────────────────────────────────────────────────────

export interface useGetCompetitionsArgs { readonly phase?: string; readonly viewportGated?: boolean; }


async function _useGetCompetitionsQueryFn(
  args: useGetCompetitionsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetCompetitionsRow>> {
  const url = '/v1/competitions';
  const params = new URLSearchParams();

  if (args.phase !== undefined) params.set('phase', String(args.phase));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCompetitions',
    routeTemplate: '/v1/competitions',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetCompetitionsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetCompetitionsFactory = Object.assign(
  (args: useGetCompetitionsArgs) => ({
    queryKey: ['competitions', 'list', { phase: args.phase ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCompetitionsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCompetitions',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['competitions', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCompetitionsArgs, PagedData<GetCompetitionsRow>>;


export const useGetCompetitionsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetCompetitionsFactory },
  parseInput: (raw: unknown): useGetCompetitionsArgs => raw as useGetCompetitionsArgs,
  parseResponse: (raw: unknown): PagedData<GetCompetitionsRow> => raw as PagedData<GetCompetitionsRow>,
  specName: 'useGetCompetitions' as const,
  polling: { hasNats: false, intervalMs: 10000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetCompetitionsRow>>;
export const useGetCompetitionsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetCompetitionsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetCompetitionsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetCompetitionsPerEntitySubjects(_args: useGetCompetitionsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetCompetitionsRowSubjectTemplates(_args: useGetCompetitionsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetCompetitionsApplyEvent(
  prev: PagedData<GetCompetitionsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetCompetitionsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetCompetitionsRealtime = {
  domain: 'competitions' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetCompetitionsArgs) => _useGetCompetitionsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetCompetitionsArgs) => _useGetCompetitionsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetCompetitionsArgs) =>
    (prev: PagedData<GetCompetitionsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetCompetitionsRow> | undefined =>
      _useGetCompetitionsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetCompetitionsRow>, unknown>;


// ─── useGetCompetition ────────────────────────────────────────────────────────────

export interface useGetCompetitionArgs { readonly competitionId: string; }


async function _useGetCompetitionQueryFn(
  args: useGetCompetitionArgs,
  ctx: QueryFunctionContext,
): Promise<GetCompetitionRow | null> {
  if (args.competitionId === undefined || args.competitionId === null || args.competitionId === '') return null;
  const url = `/v1/competitions/${args.competitionId}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCompetition',
    routeTemplate: '/v1/competitions/{competitionId}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetCompetitionRow;
}


const _useGetCompetitionFactory = Object.assign(
  (args: useGetCompetitionArgs) => ({
    queryKey: ['competitions', 'detail', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCompetitionQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCompetition',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['competitions', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCompetitionArgs, GetCompetitionRow>;


export const useGetCompetitionSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions/{competitionId}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetCompetitionFactory },
  parseInput: (raw: unknown): useGetCompetitionArgs => raw as useGetCompetitionArgs,
  parseResponse: (raw: unknown): GetCompetitionRow => raw as GetCompetitionRow,
  specName: 'useGetCompetition' as const,
  polling: { hasNats: false, intervalMs: 10000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetCompetitionRow>;


// ─── useGetCompetitionStandings ────────────────────────────────────────────────────────────

export interface useGetCompetitionStandingsArgs { readonly competitionId: string; readonly viewportGated?: boolean; }


async function _useGetCompetitionStandingsQueryFn(
  args: useGetCompetitionStandingsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetCompetitionStandingsRow>> {
  const url = `/v1/competitions/${args.competitionId}/standings`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 50));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCompetitionStandings',
    routeTemplate: '/v1/competitions/{competitionId}/standings',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetCompetitionStandingsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetCompetitionStandingsFactory = Object.assign(
  (args: useGetCompetitionStandingsArgs) => ({
    queryKey: ['competitions', 'list', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCompetitionStandingsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCompetitionStandings',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['competitions', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCompetitionStandingsArgs, PagedData<GetCompetitionStandingsRow>>;


export const useGetCompetitionStandingsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions/{competitionId}/standings',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetCompetitionStandingsFactory },
  parseInput: (raw: unknown): useGetCompetitionStandingsArgs => raw as useGetCompetitionStandingsArgs,
  parseResponse: (raw: unknown): PagedData<GetCompetitionStandingsRow> => raw as PagedData<GetCompetitionStandingsRow>,
  specName: 'useGetCompetitionStandings' as const,
  polling: { hasNats: false, intervalMs: 10000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetCompetitionStandingsRow>>;
export const useGetCompetitionStandingsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 50,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetCompetitionStandingsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetCompetitionStandingsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetCompetitionStandingsPerEntitySubjects(_args: useGetCompetitionStandingsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetCompetitionStandingsRowSubjectTemplates(_args: useGetCompetitionStandingsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetCompetitionStandingsApplyEvent(
  prev: PagedData<GetCompetitionStandingsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetCompetitionStandingsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetCompetitionStandingsRealtime = {
  domain: 'competitions' as const,
  domainPins: [] as readonly string[],
  rowKey: 'rowId' as const,
  perEntitySubjects: (args: useGetCompetitionStandingsArgs) => _useGetCompetitionStandingsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetCompetitionStandingsArgs) => _useGetCompetitionStandingsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetCompetitionStandingsArgs) =>
    (prev: PagedData<GetCompetitionStandingsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetCompetitionStandingsRow> | undefined =>
      _useGetCompetitionStandingsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetCompetitionStandingsRow>, unknown>;


// ─── useGetCompetitionFairPnlStandings ────────────────────────────────────────────────────────────

export interface useGetCompetitionFairPnlStandingsArgs { readonly competitionId: string; readonly viewportGated?: boolean; }


async function _useGetCompetitionFairPnlStandingsQueryFn(
  args: useGetCompetitionFairPnlStandingsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetCompetitionFairPnlStandingsRow>> {
  const url = `/v1/competitions/${args.competitionId}/standings/fair-pnl`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 50));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCompetitionFairPnlStandings',
    routeTemplate: '/v1/competitions/{competitionId}/standings/fair-pnl',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetCompetitionFairPnlStandingsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetCompetitionFairPnlStandingsFactory = Object.assign(
  (args: useGetCompetitionFairPnlStandingsArgs) => ({
    queryKey: ['competitions', 'fair-pnl-standings', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCompetitionFairPnlStandingsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCompetitionFairPnlStandings',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['competitions', 'fair-pnl-standings', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCompetitionFairPnlStandingsArgs, PagedData<GetCompetitionFairPnlStandingsRow>>;


export const useGetCompetitionFairPnlStandingsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions/{competitionId}/standings/fair-pnl',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetCompetitionFairPnlStandingsFactory },
  parseInput: (raw: unknown): useGetCompetitionFairPnlStandingsArgs => raw as useGetCompetitionFairPnlStandingsArgs,
  parseResponse: (raw: unknown): PagedData<GetCompetitionFairPnlStandingsRow> => raw as PagedData<GetCompetitionFairPnlStandingsRow>,
  specName: 'useGetCompetitionFairPnlStandings' as const,
  polling: { hasNats: false, intervalMs: 60000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetCompetitionFairPnlStandingsRow>>;
export const useGetCompetitionFairPnlStandingsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 50,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetCompetitionFairPnlStandingsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetCompetitionFairPnlStandingsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetCompetitionFairPnlStandingsPerEntitySubjects(_args: useGetCompetitionFairPnlStandingsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetCompetitionFairPnlStandingsRowSubjectTemplates(_args: useGetCompetitionFairPnlStandingsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetCompetitionFairPnlStandingsApplyEvent(
  prev: PagedData<GetCompetitionFairPnlStandingsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetCompetitionFairPnlStandingsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetCompetitionFairPnlStandingsRealtime = {
  domain: 'competitions' as const,
  domainPins: [] as readonly string[],
  rowKey: 'rowId' as const,
  perEntitySubjects: (args: useGetCompetitionFairPnlStandingsArgs) => _useGetCompetitionFairPnlStandingsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetCompetitionFairPnlStandingsArgs) => _useGetCompetitionFairPnlStandingsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetCompetitionFairPnlStandingsArgs) =>
    (prev: PagedData<GetCompetitionFairPnlStandingsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetCompetitionFairPnlStandingsRow> | undefined =>
      _useGetCompetitionFairPnlStandingsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetCompetitionFairPnlStandingsRow>, unknown>;


// ─── useGetCompetitionCashProfitStandings ────────────────────────────────────────────────────────────

export interface useGetCompetitionCashProfitStandingsArgs { readonly competitionId: string; readonly viewportGated?: boolean; }


async function _useGetCompetitionCashProfitStandingsQueryFn(
  args: useGetCompetitionCashProfitStandingsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetCompetitionCashProfitStandingsRow>> {
  const url = `/v1/competitions/${args.competitionId}/standings/cash-profit`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 50));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCompetitionCashProfitStandings',
    routeTemplate: '/v1/competitions/{competitionId}/standings/cash-profit',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetCompetitionCashProfitStandingsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetCompetitionCashProfitStandingsFactory = Object.assign(
  (args: useGetCompetitionCashProfitStandingsArgs) => ({
    queryKey: ['competitions', 'cash-profit-standings', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCompetitionCashProfitStandingsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCompetitionCashProfitStandings',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['competitions', 'cash-profit-standings', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCompetitionCashProfitStandingsArgs, PagedData<GetCompetitionCashProfitStandingsRow>>;


export const useGetCompetitionCashProfitStandingsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions/{competitionId}/standings/cash-profit',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetCompetitionCashProfitStandingsFactory },
  parseInput: (raw: unknown): useGetCompetitionCashProfitStandingsArgs => raw as useGetCompetitionCashProfitStandingsArgs,
  parseResponse: (raw: unknown): PagedData<GetCompetitionCashProfitStandingsRow> => raw as PagedData<GetCompetitionCashProfitStandingsRow>,
  specName: 'useGetCompetitionCashProfitStandings' as const,
  polling: { hasNats: false, intervalMs: 60000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetCompetitionCashProfitStandingsRow>>;
export const useGetCompetitionCashProfitStandingsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 50,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetCompetitionCashProfitStandingsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetCompetitionCashProfitStandingsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetCompetitionCashProfitStandingsPerEntitySubjects(_args: useGetCompetitionCashProfitStandingsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetCompetitionCashProfitStandingsRowSubjectTemplates(_args: useGetCompetitionCashProfitStandingsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetCompetitionCashProfitStandingsApplyEvent(
  prev: PagedData<GetCompetitionCashProfitStandingsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetCompetitionCashProfitStandingsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetCompetitionCashProfitStandingsRealtime = {
  domain: 'competitions' as const,
  domainPins: [] as readonly string[],
  rowKey: 'rowId' as const,
  perEntitySubjects: (args: useGetCompetitionCashProfitStandingsArgs) => _useGetCompetitionCashProfitStandingsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetCompetitionCashProfitStandingsArgs) => _useGetCompetitionCashProfitStandingsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetCompetitionCashProfitStandingsArgs) =>
    (prev: PagedData<GetCompetitionCashProfitStandingsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetCompetitionCashProfitStandingsRow> | undefined =>
      _useGetCompetitionCashProfitStandingsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetCompetitionCashProfitStandingsRow>, unknown>;


// ─── useGetCompetitionMyStanding ────────────────────────────────────────────────────────────

export interface useGetCompetitionMyStandingArgs { readonly competitionId: string; }


async function _useGetCompetitionMyStandingQueryFn(
  args: useGetCompetitionMyStandingArgs,
  ctx: QueryFunctionContext,
): Promise<GetCompetitionMyStandingRow | null> {
  if (args.competitionId === undefined || args.competitionId === null || args.competitionId === '') return null;
  const url = `/v1/competitions/${args.competitionId}/my-standing`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCompetitionMyStanding',
    routeTemplate: '/v1/competitions/{competitionId}/my-standing',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetCompetitionMyStandingRow;
}


const _useGetCompetitionMyStandingFactory = Object.assign(
  (args: useGetCompetitionMyStandingArgs) => ({
    queryKey: ['competitions', 'detail', args.competitionId, 'my-standing'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCompetitionMyStandingQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetCompetitionMyStanding',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['competitions', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCompetitionMyStandingArgs, GetCompetitionMyStandingRow>;


export const useGetCompetitionMyStandingSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/competitions/{competitionId}/my-standing',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetCompetitionMyStandingFactory },
  parseInput: (raw: unknown): useGetCompetitionMyStandingArgs => raw as useGetCompetitionMyStandingArgs,
  parseResponse: (raw: unknown): GetCompetitionMyStandingRow => raw as GetCompetitionMyStandingRow,
  specName: 'useGetCompetitionMyStanding' as const,
  polling: { hasNats: false, intervalMs: 10000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetCompetitionMyStandingRow>;
